import type { InjectionKey } from 'vue';
import type { UseConfirmDialogReturn } from '@vueuse/core';
import { useConfirmDialog } from '@vueuse/core';

type UnsavedChangesDialog = UseConfirmDialogReturn<string, boolean, boolean>;

export const UnsavedChangesDialogKey: InjectionKey<UnsavedChangesDialog> = Symbol('UnsavedChangesDialog');

/**
 * Provides a `useConfirmDialog` instance for unsaved-changes confirmation.
 *
 * Call this once in the layout that mounts `UnsavedChangesModal`.
 * The returned controls (`isRevealed`, `confirm`, `cancel`, `onReveal`)
 * should be forwarded to the modal component.
 */
export function provideUnsavedChangesDialog(): UnsavedChangesDialog {
  const dialog = useConfirmDialog<string, boolean, boolean>();
  provide(UnsavedChangesDialogKey, dialog);
  return dialog;
}

/**
 * Injects the unsaved-changes dialog controls provided by the layout.
 *
 * Falls back to `window.confirm` when no provider is found (e.g. in tests
 * or pages that are not rendered inside the private layout).
 */
export function useUnsavedChangesDialog(): Pick<UnsavedChangesDialog, 'reveal'> {
  const dialog = inject(UnsavedChangesDialogKey, null);

  if (dialog) {
    return { reveal: dialog.reveal };
  }

  return {
    reveal: (message?: string) => {
      const confirmed = window.confirm(message);
      return Promise.resolve({ data: confirmed, isCanceled: !confirmed });
    },
  };
}
